import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { ApiError } from "@/lib/api";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Textarea } from "@/components/ui/Input";
import { Modal } from "@/components/ui/Modal";
import { getAdminReport, listAdminReports, patchAdminReport } from "./api";
import {
  CATEGORY_LABEL,
  STATUS_LABEL,
  TARGET_LABEL,
  describeReportTarget,
  formatSnapshot,
  formatTimestamp,
} from "./format";
import { AdminLoadState, isAdminForbidden } from "./AdminLoadState";
import { ADMIN_FORBIDDEN_MESSAGE, REPORT_NOTE_MAX, type ReportAction, type ReportStatus, type UgcReport } from "./types";
import { isWorkspaceAdminRole } from "./validation";
import { useCurrentUgcWorkspaceRole, useUgcAdminScope } from "./useCanManageUgc";

export function ugcReportCacheKeys(userId: string, tenantId: string) {
  return {
    list: ["admin-reports", userId, tenantId] as const,
    detail: ["admin-report", userId, tenantId] as const,
  };
}

const IDLE_SCOPE = "__idle__";

export function discardScopedUgcReportQueries(
  queryClient: QueryClient,
  scope: { userId: string; tenantId: string },
): void {
  const keys = ugcReportCacheKeys(scope.userId, scope.tenantId);
  void queryClient.cancelQueries({ queryKey: keys.list });
  void queryClient.cancelQueries({ queryKey: keys.detail });
  queryClient.removeQueries({ queryKey: keys.list });
  queryClient.removeQueries({ queryKey: keys.detail });
}

const ACTION_DONE: Record<ReportAction, string> = {
  resolve: "通報を対応済みにしました。",
  dismiss: "通報を却下しました。",
  remove_message: "対象メッセージを除去しました。",
};

function SnapshotText({ snapshot }: { snapshot: unknown }): JSX.Element {
  const text = formatSnapshot(snapshot);
  return (
    <pre
      data-testid="report-snapshot"
      className="whitespace-pre-wrap break-words rounded-md bg-surface-base p-3 font-sans text-sm text-fg-primary"
    >
      {text || "（保存された対象情報はありません）"}
    </pre>
  );
}

export function ReportsAdmin(): JSX.Element {
  const scope = useUgcAdminScope();
  const { role, known: roleKnown } = useCurrentUgcWorkspaceRole();
  const roleLost = roleKnown && !isWorkspaceAdminRole(role);
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<ReportStatus>("open");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [pendingRemove, setPendingRemove] = useState<UgcReport | null>(null);
  const [accessRevoked, setAccessRevoked] = useState(false);
  const scopeKey = `${scope?.userId ?? ""}:${scope?.tenantId ?? ""}`;
  const [seenScope, setSeenScope] = useState(scopeKey);
  if (seenScope !== scopeKey) {
    setSeenScope(scopeKey);
    setSelectedId(null);
    setNote("");
    setSuccess(null);
    setError(null);
    setPendingRemove(null);
    setAccessRevoked(false);
    setStatus("open");
  }

  const userId = scope?.userId;
  const tenantId = scope?.tenantId ?? "";
  const allowFetch = !!scope && !accessRevoked && !roleLost;
  const list = useQuery({
    queryKey: allowFetch && userId != null
      ? (["admin-reports", userId, tenantId, status] as const)
      : (["admin-reports", IDLE_SCOPE] as const),
    refetchOnWindowFocus: false,
    retry: false,
    enabled: allowFetch,
    queryFn: () => listAdminReports(status),
  });
  const detail = useQuery({
    queryKey: allowFetch && userId != null
      ? (["admin-report", userId, tenantId, selectedId] as const)
      : (["admin-report", IDLE_SCOPE] as const),
    refetchOnWindowFocus: false,
    retry: false,
    enabled: allowFetch && !!selectedId,
    queryFn: () => getAdminReport(selectedId!),
  });

  const resetLocal = () => {
    setSelectedId(null);
    setNote("");
    setSuccess(null);
    setError(null);
    setPendingRemove(null);
  };

  const prevUserIdRef = useRef(userId);
  const prevTenantIdRef = useRef(tenantId);
  useEffect(() => {
    const prevUserId = prevUserIdRef.current;
    const prevTenantId = prevTenantIdRef.current;
    prevUserIdRef.current = userId;
    prevTenantIdRef.current = tenantId;
    if (prevUserId != null && (prevUserId !== userId || prevTenantId !== tenantId)) {
      discardScopedUgcReportQueries(queryClient, { userId: prevUserId, tenantId: prevTenantId ?? "" });
    }
  }, [queryClient, userId, tenantId]);

  useEffect(() => {
    if (isAdminForbidden(list.error) || isAdminForbidden(detail.error) || roleLost) {
      setAccessRevoked(true);
    }
  }, [list.error, detail.error, roleLost]);

  useEffect(() => {
    if (!accessRevoked && !roleLost) return;
    resetLocal();
    if (userId != null) discardScopedUgcReportQueries(queryClient, { userId, tenantId });
  }, [accessRevoked, roleLost, queryClient, userId, tenantId]);

  const reports = list.data?.reports ?? [];
  const report = detail.data?.report ?? reports.find((item) => item.id === selectedId);
  const forbidden = accessRevoked || roleLost || isAdminForbidden(list.error) || isAdminForbidden(detail.error);
  const hideBody = forbidden || !list.data;

  const act = async (action: ReportAction, target: UgcReport) => {
    if (busy || forbidden) return;
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const result = await patchAdminReport(target.id, action, note);
      setPendingRemove(null);
      setSuccess(ACTION_DONE[action]);
      setNote("");
      await list.refetch();
      if (selectedId === target.id) {
        await detail.refetch();
      }
      if (result.report.status !== status) setSelectedId(result.report.id);
    } catch (e) {
      setSuccess(null);
      if (isAdminForbidden(e)) {
        setAccessRevoked(true);
        return;
      }
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <header className="mb-6 border-b border-border-subtle pb-4">
        <h1 className="text-xl font-semibold text-fg-primary">通報管理</h1>
        <p className="mt-1 text-sm text-fg-secondary">
          ワークスペース内の通報を確認し、却下・対応済み・メッセージ除去を実行します。
        </p>
      </header>

      {list.isPending && !forbidden && (
        <p role="status" className="text-sm text-fg-secondary">
          通報を読み込んでいます…
        </p>
      )}
      {(list.error || forbidden) && (
        <AdminLoadState
          error={list.error ?? detail.error ?? new ApiError(403, ADMIN_FORBIDDEN_MESSAGE)}
          onRetry={() => {
            if (accessRevoked || roleLost) {
              setAccessRevoked(false);
              resetLocal();
              return;
            }
            void list.refetch();
          }}
        />
      )}

      {!hideBody && (
        <section className="mb-4 overflow-hidden rounded-lg bg-canvas shadow-sm ring-1 ring-border-subtle">
          <header className="flex flex-wrap items-center gap-3 border-b border-border-subtle px-5 py-3">
            <h2 className="text-sm font-semibold text-fg-primary">通報一覧</h2>
            <label className="ml-auto text-xs text-fg-secondary">
              状態
              <select
                aria-label="通報の状態"
                className="ml-2 rounded-md border border-border-default bg-canvas px-2 py-1 text-sm"
                value={status}
                disabled={busy}
                onChange={(e) => {
                  setStatus(e.target.value as ReportStatus);
                  resetLocal();
                }}
              >
                <option value="open">未対応</option>
                <option value="resolved">対応済み</option>
                <option value="dismissed">却下</option>
              </select>
            </label>
          </header>
          <div className="p-5">
            {reports.length === 0 ? (
              <p role="status" className="py-8 text-center text-sm text-fg-muted">
                この状態の通報はありません。
              </p>
            ) : (
              <div className="overflow-x-auto rounded-md ring-1 ring-border-subtle">
                <table className="w-full text-sm">
                  <thead className="bg-surface-base text-left text-xs text-fg-muted">
                    <tr>
                      <th className="px-4 py-2 font-medium">作成時刻</th>
                      <th className="px-4 py-2 font-medium">カテゴリ</th>
                      <th className="px-4 py-2 font-medium">状態</th>
                      <th className="px-4 py-2 font-medium">対象</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border-subtle bg-canvas">
                    {reports.map((item) => (
                      <tr key={item.id} className={item.id === selectedId ? "bg-brand-50" : "hover:bg-surface-base"}>
                        <td className="px-4 py-2.5">
                          <button
                            type="button"
                            aria-label={`通報 ${item.id} を開く`}
                            className="text-left text-fg-primary underline-offset-2 hover:underline"
                            onClick={() => {
                              setSelectedId(item.id);
                              setSuccess(null);
                              setError(null);
                            }}
                          >
                            {formatTimestamp(item.created_at)}
                          </button>
                        </td>
                        <td className="px-4 py-2.5">{CATEGORY_LABEL[item.category] ?? item.category}</td>
                        <td className="px-4 py-2.5">
                          <Badge tone={item.status === "open" ? "warning" : item.status === "resolved" ? "success" : "neutral"}>
                            {STATUS_LABEL[item.status] ?? item.status}
                          </Badge>
                        </td>
                        <td className="px-4 py-2.5 text-fg-secondary">
                          {TARGET_LABEL[item.target_type] ?? item.target_type} {item.target_id}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>
      )}

      {!hideBody && selectedId && (
        <section className="mb-4 overflow-hidden rounded-lg bg-canvas shadow-sm ring-1 ring-border-subtle">
          <header className="border-b border-border-subtle px-5 py-3">
            <h2 className="text-sm font-semibold text-fg-primary">通報の詳細</h2>
          </header>
          <div className="space-y-4 p-5">
            {detail.isPending && (
              <p role="status" className="text-sm text-fg-secondary">
                詳細を読み込んでいます…
              </p>
            )}
            {detail.error && !isAdminForbidden(detail.error) && (
              <AdminLoadState error={detail.error} onRetry={() => void detail.refetch()} />
            )}
            {report && !forbidden && (
              <>
                <dl className="grid gap-3 text-sm md:grid-cols-2">
                  <div>
                    <dt className="text-xs text-fg-muted">作成時刻</dt>
                    <dd>{formatTimestamp(report.created_at)}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-fg-muted">カテゴリ</dt>
                    <dd>{CATEGORY_LABEL[report.category] ?? report.category}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-fg-muted">状態</dt>
                    <dd>{STATUS_LABEL[report.status] ?? report.status}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-fg-muted">対象</dt>
                    <dd>{describeReportTarget(report)}</dd>
                  </div>
                  <div className="md:col-span-2">
                    <dt className="text-xs text-fg-muted">詳細</dt>
                    <dd className="whitespace-pre-wrap break-words">{report.details || "—"}</dd>
                  </div>
                  {report.resolution_note ? (
                    <div className="md:col-span-2">
                      <dt className="text-xs text-fg-muted">対応メモ</dt>
                      <dd className="whitespace-pre-wrap break-words">{report.resolution_note}</dd>
                    </div>
                  ) : null}
                </dl>
                <div>
                  <p className="mb-1 text-xs font-medium text-fg-secondary">保存された対象情報</p>
                  <SnapshotText snapshot={report.snapshot} />
                </div>
                <label className="flex flex-col gap-1.5">
                  <span className="text-xs font-medium text-fg-secondary">対応メモ（任意）</span>
                  <Textarea
                    aria-label="対応メモ"
                    rows={3}
                    maxLength={REPORT_NOTE_MAX}
                    value={note}
                    disabled={busy || report.status !== "open"}
                    onChange={(e) => setNote(e.target.value)}
                  />
                </label>
                {busy && (
                  <p role="status" className="text-sm text-fg-secondary">
                    処理中…
                  </p>
                )}
                {error && (
                  <p role="alert" className="text-sm text-danger">
                    {error}
                  </p>
                )}
                {success && <p role="status">{success}</p>}
                {report.status === "open" && (
                  <div className="flex flex-wrap gap-2">
                    <Button disabled={busy} onClick={() => void act("resolve", report)}>
                      対応済みにする
                    </Button>
                    <Button disabled={busy} variant="secondary" onClick={() => void act("dismiss", report)}>
                      却下する
                    </Button>
                    {report.target_type === "message" && (
                      <Button disabled={busy} variant="danger" onClick={() => setPendingRemove(report)}>
                        メッセージを除去
                      </Button>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      )}

      <Modal
        open={!!pendingRemove && !forbidden}
        onClose={() => {
          if (!busy) setPendingRemove(null);
        }}
        title="メッセージを除去しますか？"
        description="この通報の対象メッセージを会話から除去します。"
      >
        {pendingRemove && !forbidden && (
          <div className="space-y-3">
            <p className="text-sm">{describeReportTarget(pendingRemove)}</p>
            <SnapshotText snapshot={pendingRemove.snapshot} />
            <div className="flex justify-end gap-2">
              <Button variant="ghost" disabled={busy} onClick={() => setPendingRemove(null)}>
                キャンセル
              </Button>
              <Button
                variant="danger"
                disabled={busy}
                onClick={() => void act("remove_message", pendingRemove)}
              >
                除去する
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}
